/**
 * accountCleanups.ts — Accounts S7 (spec §3.2 Clean-ups tile "Fix all ›";
 * ruling of 2026-10-27): the rules and copy of the Fix all preview, pure.
 * Every fix comes from GET /v1/sales/accounts/cleanups — the tidied names
 * and each pair's sides and survivor are the server's, never counted here.
 *
 * Selection: every tidy starts selected (one batch, one Undo); a merge
 * starts selected only when its confidence is high — a medium one (a name
 * that only starts the same) is never pre-selected. Each merge still needs
 * its own confirm (S6's dialog), owner/admin only.
 */
import type { AccountMergeSide, AccountMergeSuggestion, AccountTidySkipped, SalesAccountCleanups, SalesAccountTidies, SalesAccountTidiesUndo } from '../../../../api/types'
import { DUPLICATE_LABEL } from './accountsView'
import { movedPhrase, plural } from './accountMergeActions'

/** A merge suggestion's identity in a selection: its two ids, in wire order. */
export const mergeKey = (m: AccountMergeSuggestion): string => m.accounts.map(a => a.id).join('|')

export function survivorOf(m: AccountMergeSuggestion): AccountMergeSide {
  return m.accounts.find(a => a.id === m.suggestedSurvivorId) ?? m.accounts[0]
}

export function mergedOf(m: AccountMergeSuggestion): AccountMergeSide {
  return m.accounts.find(a => a.id !== m.suggestedSurvivorId) ?? m.accounts[1]
}

/** What Fix all selects before the user touches anything. */
export function defaultSelection(c: SalesAccountCleanups): { tidies: Set<string>; merges: Set<string> } {
  return {
    tidies: new Set(c.tidies.map(t => t.accountId)),
    merges: new Set(c.merges.filter(m => m.confidence === 'high').map(mergeKey)),
  }
}

/** "Merge "face" into Facebook". */
export function mergeTitle(m: AccountMergeSuggestion): string {
  return `Merge "${mergedOf(m).name}" into ${survivorOf(m).name}`
}

/** What the merge would do, from the preview's counts. */
export function mergeLine(m: AccountMergeSuggestion): string {
  const merged = mergedOf(m), survivor = survivorOf(m)
  return `Moves ${movedPhrase(merged.contacts, merged.deals)} to ${survivor.name}. "${merged.name}" is kept as another name for ${survivor.name}.`
}

/** The reason label, and for a medium match the reason to look first. */
export function mergeReason(m: AccountMergeSuggestion): string {
  const label = DUPLICATE_LABEL[m.reason] ?? 'Possible duplicate'
  return m.confidence === 'high' ? label : `${label} · check it is the same company`
}

const SKIP_WHY: Record<string, string> = {
  changed: 'its name changed since this list was read',
  merged: 'it was merged into another account',
  gone: 'it is no longer an account',
  already: 'it already has its old name back',
}

function skippedSentence(left: AccountTidySkipped[]): string {
  return left.map(s => `"${s.name}" (${SKIP_WHY[s.reason] ?? 'it changed'})`).join(', ')
}

/** The tidy batch's one toast. */
export function tidiedToast(r: SalesAccountTidies): { title: string; description?: string } {
  const title = r.tidied.length === 0 ? 'No names were tidied' : `Tidied ${plural(r.tidied.length, 'name')}`
  return r.skipped.length === 0 ? { title } : { title, description: `Left as ${r.skipped.length === 1 ? 'it is' : 'they are'}: ${skippedSentence(r.skipped)}.` }
}

/** The tidy Undo's toast. */
export function tidyUndoneToast(u: SalesAccountTidiesUndo): { title: string; description?: string } {
  const title = u.restored.length === 0 ? 'No names were changed back' : `Tidy undone: ${plural(u.restored.length, 'name')} changed back`
  return u.notRestored.length === 0 ? { title } : { title, description: `Left as ${u.notRestored.length === 1 ? 'it is' : 'they are'}: ${skippedSentence(u.notRestored)}.` }
}
